import axios from "axios";
import { getApiBase } from "../apiBase";
import { uploadProjectFieldFile } from "./projectFieldFilesApi";
import bundledCustomers from "./customersList";

async function sendNewWorkOrderEmail(projectId, getAuthHeaders) {
  const base = getApiBase();
  try {
    await axios.post(
      `${base}/projects/${projectId}/phase-trigger-email`,
      { triggerId: "new_work_order" },
      { headers: getAuthHeaders() }
    );
  } catch (err) {
    console.error("New work order email failed:", err?.response?.data?.error || err.message);
  }
}

async function sendNewCustomerEmail(projectId, getAuthHeaders) {
  const base = getApiBase();
  try {
    await axios.post(
      `${base}/projects/${projectId}/phase-trigger-email`,
      { triggerId: "new_customer" },
      { headers: getAuthHeaders() }
    );
  } catch (err) {
    console.error("New customer email failed:", err?.response?.data?.error || err.message);
  }
}

function payloadIsNewCustomer(payload) {
  const raw = payload?.new_customer ?? payload?.newCustomer;
  return raw === true || raw === "true" || raw === 1 || raw === "1";
}

export async function createCommercialProject(payload, { apiUrl, getAuthHeaders, signedServiceOrderFile }) {
  const base = apiUrl || getApiBase();
  const { data } = await axios.post(
    `${base}/projects/commercial/create`,
    { ...payload, defer_new_work_order_email: true },
    { headers: getAuthHeaders() }
  );

  if (data?.project?.id != null) {
    if (signedServiceOrderFile) {
      await uploadProjectFieldFile(
        data.project.id,
        ["project_summary", "service_order"],
        signedServiceOrderFile,
        getAuthHeaders
      );
    }
    await sendNewWorkOrderEmail(data.project.id, getAuthHeaders);
    if (payloadIsNewCustomer(payload) && String(payload?.project_type || payload?.projectType || "Revenue") === "Revenue") {
      await sendNewCustomerEmail(data.project.id, getAuthHeaders);
    }
  }

  return data;
}

export async function addCommercialWorkOrderChild(
  parentWorkOrder,
  payload,
  { apiUrl, getAuthHeaders, signedServiceOrderFile } = {}
) {
  const base = apiUrl || getApiBase();
  const parentWo = String(parentWorkOrder || "").trim();
  const { data } = await axios.post(
    `${base}/projects/commercial/${encodeURIComponent(parentWo)}/add-child`,
    payload,
    { headers: getAuthHeaders() }
  );

  if (data?.project?.id != null && signedServiceOrderFile) {
    await uploadProjectFieldFile(
      data.project.id,
      ["project_summary", "service_order"],
      signedServiceOrderFile,
      getAuthHeaders
    );
  }

  return data;
}

export async function addExpansionSubsegment(
  parentWorkOrder,
  payload,
  { apiUrl, getAuthHeaders } = {}
) {
  const base = apiUrl || getApiBase();
  const parentWo = String(parentWorkOrder || "").trim();
  const { data } = await axios.post(
    `${base}/projects/expansion/${encodeURIComponent(parentWo)}/add-subsegment`,
    payload,
    { headers: getAuthHeaders() }
  );
  return data;
}

/** Distinct customer names from commercial project rows. */
export function collectCustomerOptions(rows) {
  const set = new Set();
  for (const row of rows || []) {
    const c = row?.customer != null ? String(row.customer).trim() : "";
    if (c) set.add(c);
    const summary = row?.acf_groups_json?.project_summary || row?.acf_groups_json?.["Project Summary Test 2"];
    const fromSummary = summary?.customer || summary?.customer_name;
    if (fromSummary != null && String(fromSummary).trim()) set.add(String(fromSummary).trim());
  }
  return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/** Bundled deduped customer list (see customersList.js; regenerate via scripts/csv-to-customers-json.cjs). */
export async function fetchBundledCustomerList() {
  return Array.isArray(bundledCustomers) ? [...bundledCustomers] : [];
}

export function mergeCustomerOptionLists(...lists) {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    for (const item of list || []) {
      const name = String(item || "").trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(name);
    }
  }
  return out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

export function customerNameExists(name, options) {
  const q = String(name || "").trim().toLowerCase();
  if (!q) return false;
  return (options || []).some((opt) => String(opt).trim().toLowerCase() === q);
}

/** Distinct sales rep names from project rows. */
export function collectSalesRepOptions(rows) {
  const set = new Set();
  for (const row of rows || []) {
    const rep = row?.sales_rep != null ? String(row.sales_rep).trim() : "";
    if (rep) set.add(rep);
    const summary = row?.acf_groups_json?.project_summary || row?.acf_groups_json?.["Project Summary Test 2"];
    const fromSummary = summary?.sales_rep || summary?.sales_representative;
    if (fromSummary != null && String(fromSummary).trim()) set.add(String(fromSummary).trim());
  }
  return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}
